import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress, Grid, IconButton, TextField, Tooltip } from "@mui/material";import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, isValid, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "./ConfirmDialog";
import Notification from "./Notification";

export const toDateInputValue = (value) => {
  if (value == null || value === "") {
    return "";
  }

  const stringValue = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
    return stringValue.slice(0, 10);
  }

  const date = new Date(stringValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

export const getClaimPaymentEndpoint = (claimId, claimType = "vat") =>
  claimType === "excise" ? `/api/v1/exciseClaims/${claimId}/payment` : `/api/v1/claims/${claimId}/payment`;

const ClaimPaymentFields = ({
  claimId,
  claimType = "vat",
  paymentReceivedDate,
  paymentAmount,
  onSaved,
  onSaveSuccess,
}) => {
  const { t } = useTranslation();
  const [paymentReceivedDateInput, setPaymentReceivedDateInput] = useState("");
  const [paymentAmountInput, setPaymentAmountInput] = useState("");
  const [savingPayment, setSavingPayment] = useState(false);
  const [removingPayment, setRemovingPayment] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: "", type: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const isPaymentBusy = savingPayment || removingPayment;

  const paymentReceivedDateValue = useMemo(() => {
    if (!paymentReceivedDateInput) {
      return null;
    }

    const parsed = parseISO(paymentReceivedDateInput);
    return isValid(parsed) ? parsed : null;
  }, [paymentReceivedDateInput]);

  useEffect(() => {
    if (!claimId) {
      return;
    }

    setPaymentReceivedDateInput(toDateInputValue(paymentReceivedDate));
    setPaymentAmountInput(paymentAmount != null && paymentAmount !== "" ? String(paymentAmount) : "");
  }, [claimId, paymentReceivedDate, paymentAmount]);

  const showNotify = (type, message) => {
    setNotify({ isOpen: true, message, type });
  };

  const persistClaimPayment = async (nextPaymentReceivedDate, nextPaymentAmount) => {
    if (!claimId) {
      return;
    }

    setSavingPayment(true);
    try {
      await axios.put(getClaimPaymentEndpoint(claimId, claimType), {
        paymentReceivedDate: nextPaymentReceivedDate,
        paymentAmount: nextPaymentAmount,
      });
      showNotify("success", t("Payment information saved successfully."));
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      if (typeof onSaved === "function") {
        await onSaved();
      }
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        showNotify("error", t("Claim not found"));
      } else {
        showNotify("error", t("Error saving payment information"));
      }
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    } finally {
      setSavingPayment(false);
    }
  };

  const removeClaimPayment = async () => {
    if (!claimId) {
      return;
    }

    setRemovingPayment(true);
    try {
      await axios.put(getClaimPaymentEndpoint(claimId, claimType), {
        paymentReceivedDate: null,
        paymentAmount: null,
      });
      showNotify("success", t("Payment information removed successfully."));
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      if (typeof onSaved === "function") {
        await onSaved();
      }
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        showNotify("error", t("Claim not found"));
      } else {
        showNotify("error", t("Error removing payment information"));
      }
      setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
    } finally {
      setRemovingPayment(false);
    }
  };

  const handleSavePaymentClick = () => {
    if (!paymentReceivedDateInput) {
      showNotify("error", t("Payment date is required"));
      return;
    }

    const sanitized = String(paymentAmountInput).replace(",", ".");
    const amountFloat = parseFloat(sanitized);
    if (String(paymentAmountInput).trim() === "" || Number.isNaN(amountFloat) || amountFloat <= 0.01) {
      showNotify("error", t("Payment amount should be a decimal and higher than 0.01"));
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: t("Confirm payment information"),
      subTitle: t("Are you sure you want to save the payment date and amount for this claim?"),
      onConfirm: () => {
        void persistClaimPayment(paymentReceivedDateInput, amountFloat);
      },
    });
  };

  const handleRemovePaymentClick = () => {
    setConfirmDialog({
      isOpen: true,
      title: t("Confirm remove payment information"),
      subTitle: t("Are you sure you want to remove the payment date and amount for this claim?"),
      onConfirm: () => {
        void removeClaimPayment();
      },
    });
  };

  return (
    <>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={5} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
            <DatePicker
              label={t("Payment received date")}
              value={paymentReceivedDateValue}
              onChange={(newValue) => {
                if (!newValue) {
                  setPaymentReceivedDateInput("");
                  return;
                }
                if (!isValid(newValue)) {
                  return;
                }
                setPaymentReceivedDateInput(format(newValue, "yyyy-MM-dd"));
              }}
              inputFormat="dd-MM-yyyy"
              disabled={isPaymentBusy}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  variant="outlined"
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": t("Payment received date"),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={5} md={4}>
          <TextField
            label={t("Payment amount")}
            type="text"
            size="small"
            fullWidth
            value={paymentAmountInput}
            onChange={(e) => setPaymentAmountInput(e.target.value)}
            disabled={isPaymentBusy}
            inputMode="decimal"
            inputProps={{ "aria-label": t("Payment amount") }}
            variant="outlined"
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Tooltip title={t("Save payment information")}>
            <span>
              <IconButton
                type="button"
                color="primary"
                size="small"
                onClick={handleSavePaymentClick}
                disabled={isPaymentBusy}
                aria-label={t("Save payment information")}
                sx={{
                  border: 1,
                  borderColor: "primary.main",
                  borderRadius: 1,
                }}
              >
                {savingPayment ? <CircularProgress size={24} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: 24 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("Remove payment information")}>
            <span>
              <IconButton
                type="button"
                color="error"
                size="small"
                onClick={handleRemovePaymentClick}
                disabled={isPaymentBusy}
                aria-label={t("Remove payment information")}
                sx={{
                  border: 1,
                  borderColor: "error.main",
                  borderRadius: 1,
                }}
              >
                {removingPayment ? <CircularProgress size={24} color="inherit" /> : <DeleteOutlineIcon sx={{ fontSize: 24 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>    </>
  );
};

export default ClaimPaymentFields;
